import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Stats from 'stats.js';
//import { easeInCubic } from 'easing-utils';
//import Starfield from './Starfield';
//import Nebula from './Nebula';
import SpaceBackground from './SpaceBackground';
import AsteroidScene from './AsteroidScene';
import PlanetScene from './PlanetScene';
import SpaceStationScene from './SpaceStationScene';

import { isMobile } from 'react-device-detect';
import innerHeight from 'ios-inner-height';

const THREE = (window.THREE = require('three'));

require('three/examples/js/shaders/CopyShader');
require('three/examples/js/shaders/FXAAShader');
require('three/examples/js/postprocessing/EffectComposer');
require('three/examples/js/postprocessing/OutlinePass');
require('three/examples/js/postprocessing/RenderPass');
require('three/examples/js/postprocessing/ShaderPass');
require('three/examples/js/loaders/DDSLoader');
require('three/examples/js/loaders/FBXLoader');

const dat = require('three/examples/js/libs/dat.gui.min.js');
const Zlib = require('three/examples/js/libs/inflate.min.js');
window.Zlib = Object.assign({}, Zlib.Zlib);

const DEFAULT_LAYER = 0;
const OCCLUSION_LAYER = 1;
//const FPS_THRESHOLD = 45;

THREE.VolumetricLightShader = {
  uniforms: {
    tDiffuse: { value: null },
    lightPosition: {
      value: new THREE.Vector2(0, 0),
    },
    exposure: { value: 0.17 },
    decay: { value: 0.968 },
    density: { value: 0.98 },
    weight: { value: 0.43 },
    samples: { value: 15 },
    //samples: { value: 50 },
  },

  vertexShader: [
    'varying vec2 vUv;',
    'void main() {',
    'vUv = uv;',
    'gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );',
    '}',
  ].join('\n'),

  fragmentShader: [
    'varying vec2 vUv;',
    'uniform sampler2D tDiffuse;',
    'uniform vec2 lightPosition;',
    'uniform float exposure;',
    'uniform float decay;',
    'uniform float density;',
    'uniform float weight;',
    'uniform int samples;',
    'const int MAX_SAMPLES = 15;',
    'void main()',
    '{',
    'vec2 texCoord = vUv;',
    'vec2 deltaTextCoord = texCoord - lightPosition;',
    'deltaTextCoord *= 1.0 / float(samples) * density;',
    'vec4 color = texture2D(tDiffuse, texCoord);',
    'float illuminationDecay = .5;',
    'for(int i=0; i < MAX_SAMPLES; i++)',
    '{',
    'if(i == samples){',
    'break;',
    '}',
    'texCoord -= deltaTextCoord;',
    'vec4 sample = texture2D(tDiffuse, texCoord);',
    'sample *= illuminationDecay * weight;',
    'color += sample;',
    'illuminationDecay *= decay;',
    '}',
    'gl_FragColor = color * exposure;',
    '}',
  ].join('\n'),
};

THREE.AdditiveBlendingShader = {
  uniforms: {
    tDiffuse: { value: null },
    tAdd: { value: null },
  },

  vertexShader: [
    'varying vec2 vUv;',
    'void main() {',
    'vUv = uv;',
    'gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );',
    '}',
  ].join('\n'),

  fragmentShader: [
    'uniform sampler2D tDiffuse;',
    'uniform sampler2D tAdd;',
    'varying vec2 vUv;',
    'void main() {',
    'vec4 color = texture2D( tDiffuse, vUv );',
    'vec4 add = texture2D( tAdd, vUv );',
    'gl_FragColor = color + add;',
    '}',
  ].join('\n'),
};

THREE.MixShader = {
  uniforms: {
    tDiffuse: { value: null },
    tAdd: { value: null },
    uProgress: { value: 1, type: 'f' },
  },

  vertexShader: [
    'varying vec2 vUv;',
    'void main() {',
    'vUv = uv;',
    //'gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );',
    'vec4 tmpvar_1;',
    'tmpvar_1.w = 1.0;',
    'tmpvar_1.xyz = position;',
    'gl_Position = ((projectionMatrix * modelViewMatrix) * tmpvar_1);',
    '}',
  ].join('\n'),

  fragmentShader: [
    'uniform sampler2D tDiffuse;',
    'uniform sampler2D tAdd;',
    'uniform float uProgress;',
    'varying vec2 vUv;',
    'void main() {',
    //'vec4 color = texture2D( tDiffuse, vUv );',
    //'vec4 add = texture2D( tAdd, vUv );',
    //'gl_FragColor = mix(color, add, uProgress);',
    'lowp vec4 tmpvar_1;',
    'tmpvar_1 = mix (texture2D (tDiffuse, vUv), texture2D (tAdd, vUv), uProgress);',
    'gl_FragColor = tmpvar_1;',
    '}',
  ].join('\n'),
};

const statsEnabled = false;

const Container = styled.div`
  width: 100vw;
  height: 100vh;
  position: fixed;
  pointer-events: none;

  canvas {
    pointer-events: none;
    position: absolute;
    top: 0;
    left: 0;
  }
`;

let lastTime = new Date().getTime();

class SpaceScene extends Component {
  progress = 0;
  globalScale = 1;
  sunScale = 0;
  disableConstantRender = false;
  lastProgress = undefined;
  occlusionEnabled = false;
  ease = 0;
  deltaEase = 0;

  supportsS3 = false;
  ddsLoader = null;

  static contextTypes = {
    registerMenuTransitionElement: PropTypes.func.isRequired,
    unregisterMenuTransitionElement: PropTypes.func.isRequired,
  };

  static propTypes = {
    onWebGlLoaded: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);

    this.animate = this.animate.bind(this);

    this.manager = new THREE.LoadingManager();
    /*
    this.manager.onProgress = (item, loaded, total) => {
      console.log(item, loaded, total);
    };
    */
    this.manager.onLoad = () => {
      this.start();
    };
    this.textureLoader = new THREE.TextureLoader(this.manager);
    this.textureLoader.load(
      `${process.env.PUBLIC_URL}/webgl_monochrome/13-12-2017/SA_Logo.png`
    );
    this.textureLoader.load(
      `${
        process.env.PUBLIC_URL
      }/webgl_monochrome/13-12-2017/Station_Atlas_01.png`
    );
    this.fbxLoader = new THREE.FBXLoader(this.manager);
    if (statsEnabled) {
      this.stats = new Stats();
      document.body.appendChild(this.stats.dom);
    }
  }

  componentDidMount = () => {
    this.init();
  };

  init = () => {
    window.addEventListener('resize', this.onResize);

    this.initRenderer();
    this.initScene();
    this.initCamera();
    this.initLights();
    this.initSpace();
    //this.initStarfield();
    this.initPlanets();
    this.initAsteroids();
    this.initSpaceStation();
    this.initPostProcessing();
    //this.initGUI();

    if (this.occlusionEnabled) {
      this.updateShaderLight();
    }

    this.mount.appendChild(this.renderer.domElement);
    this.elID = this.context.registerMenuTransitionElement(this.updateProgress);

    this.onResize();

    this.renderer.clear();
  };

  onResize = () => {
    const width = window.innerWidth;
    const height = !isMobile ? window.innerHeight : innerHeight();

    this.globalScale = Math.max(width / 1920, height / 1080);
    this.spaceBackground.scale.setScalar(this.globalScale * 1.5);
    //this.starfield.scale.setScalar(this.globalScale);
    //this.nebula.scale.setScalar(this.globalScale * 1.5);

    this.lensFlare.lensFlares.forEach(flare => {
      flare.scale = this.globalScale;
    });

    this.wireRenderTarget.setSize(width, height);

    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();

    this.bgCamera.left = width / -2;
    this.bgCamera.right = width / 2;
    this.bgCamera.top = height / 2;
    this.bgCamera.bottom = height / -2;
    this.bgCamera.updateProjectionMatrix();

    this.fxaaPass.uniforms.resolution.value.x = 1 / width;
    this.fxaaPass.uniforms.resolution.value.y = 1 / height;

    if (this.occlusionEnabled) {
      this.occlusionRenderTarget.setSize(width * 0.125, height * 0.125);
      this.updateShaderLight();
    }
    this.renderer.setSize(width, height);
    this.composer.setSize(width, height);
  };

  initRenderer = () => {
    const width = this.mount.clientWidth;
    const height = this.mount.clientHeight;

    const renderer = new THREE.WebGLRenderer({
      antialias: false,
      alpha: false,
      stencil: false,
    });
    renderer.setClearColor(new THREE.Color(0xffffff));
    renderer.setSize(width, height);

    renderer.shadowMap.enabled = !isMobile;
    this.renderer = renderer;
    try {
      this.supportsS3 =
        renderer.extensions.get('WEBGL_compressed_texture_s3tc') !== null;
    } catch (e) {}
    if (this.supportsS3) {
      this.ddsLoader = new THREE.DDSLoader(this.manager);
    }
    /*
    let disableConstantRender = this.disableConstantRender;
    try {
      disableConstantRender =
        this.renderer.context.getParameter(
          this.renderer.context.MAX_COMBINED_TEXTURE_IMAGE_UNITS
        ) <= 16;
    } catch (e) {
      console.log(e);
    }

    this.disableConstantRender = disableConstantRender;
    */
  };

  initScene = () => {
    const scene = new THREE.Scene();
    this.scene = scene;
  };

  initCamera = () => {
    const width = this.mount.clientWidth;
    const height = this.mount.clientHeight;
    const fov = 20;
    const aspect = width / height;

    const bgCamera = new THREE.OrthographicCamera(
      width / -2,
      width / 2,
      height / 2,
      height / -2,
      100,
      1000000
    );
    bgCamera.layers.set(2);
    this.bgCamera = bgCamera;

    const camera = new THREE.PerspectiveCamera(fov, aspect, 100, 1000000);
    camera.position.z = 5000;
    this.camera = camera;
  };

  initLights = () => {
    const ambientLight = new THREE.AmbientLight(0x191919, 0.75);
    this.scene.add(ambientLight);

    const spotLight = new THREE.SpotLight(0xffffff, 1.0, 0.0, 2.592, 0.0, 0.0);

    spotLight.intensity = 0.376;
    spotLight.distance = 428054;
    spotLight.angle = 0.11;
    spotLight.penumbra = 1;
    spotLight.decay = 1.5;
    spotLight.position.set(-10000, 24418, -92487);
    this.spotLight = spotLight;

    this.scene.add(spotLight);

    const spotLight2 = new THREE.SpotLight(
      0xffffff,
      3.0,
      20000.0,
      0.175,
      0.031,
      0.0
    );
    spotLight2.position.set(10000, 2361, -8000);
    spotLight2.castShadow = true;
    /*
    spotLight2.shadow.mapSize.width = 4096;
    spotLight2.shadow.mapSize.height = 4096;
    */
    spotLight2.shadow.mapSize.width = 1024;
    spotLight2.shadow.mapSize.height = 1024;
    spotLight2.shadow.camera.near = 1000;
    spotLight2.shadow.camera.far = 1000000;
    spotLight2.shadow.bias = 0.0000045;

    spotLight2.intensity = 3;
    spotLight2.distance = 20000;
    spotLight2.angle = 0.18;
    spotLight2.penumbra = 0.296;
    spotLight2.decay = 0;
    spotLight2.position.set(2361, 155, -6462);

    this.spotLight2 = spotLight2;

    this.spotLight4 = spotLight2.clone();
    this.spotLight4.castShadow = false;
    this.spotLight4.layers.set(100);

    this.scene.add(spotLight2);
    this.scene.add(this.spotLight4);
  };

  initPostProcessing = () => {
    const width = window.innerWidth;
    const height = !isMobile ? window.innerHeight : innerHeight();

    if (this.occlusionEnabled) {
      this.occlusionRenderTarget = new THREE.WebGLRenderTarget(
        width * 0.125,
        height * 0.125,
        {
          stencilBuffer: false,
          depthBuffer: false,
        }
      );
      this.occlusionComposer = new THREE.EffectComposer(
        this.renderer,
        this.occlusionRenderTarget
      );

      this.occlusionComposer.addPass(
        new THREE.RenderPass(this.scene, this.camera, null, 0x0, 0)
      );
      const volumetricLightPass = new THREE.ShaderPass(
        THREE.VolumetricLightShader
      );
      volumetricLightPass.needsSwap = false;
      this.volumetricLightPass = volumetricLightPass;
      this.occlusionComposer.addPass(volumetricLightPass);
    }

    this.composer = new THREE.EffectComposer(this.renderer);
    const bgPass = new THREE.RenderPass(this.scene, this.bgCamera);
    this.bgPass = bgPass;
    this.composer.addPass(this.bgPass);
    const fgPass = new THREE.RenderPass(this.scene, this.camera);
    fgPass.clear = false;
    this.fgPass = fgPass;
    this.composer.addPass(this.fgPass);

    if (this.occlusionEnabled) {
      const additivePass = new THREE.ShaderPass(THREE.AdditiveBlendingShader);
      additivePass.uniforms.tAdd.value = this.occlusionRenderTarget.texture;
      this.additivePass = additivePass;
      this.composer.addPass(this.additivePass);
    }

    this.wireRenderTarget = new THREE.WebGLRenderTarget(width, height, {
      stencilBuffer: false,
    });

    const mixPass = new THREE.ShaderPass(THREE.MixShader);
    mixPass.uniforms.tAdd.value = this.wireRenderTarget.texture;
    this.mixPass = mixPass;
    this.composer.addPass(mixPass);

    // Add FXAA pass
    const fxaaPass = new THREE.ShaderPass(THREE.FXAAShader);
    fxaaPass.uniforms.resolution.value.x = 1 / width;
    fxaaPass.uniforms.resolution.value.y = 1 / height;
    fxaaPass.renderToScreen = true;
    this.fxaaPass = fxaaPass;

    this.composer.addPass(fxaaPass);
  };

  initAsteroids = () => {
    this.asteroidScene = new AsteroidScene(this.occlusionEnabled);
    this.scene.add(this.asteroidScene);
  };

  initSpaceStation = () => {
    this.spaceStationScene = new SpaceStationScene(
      this.fbxLoader,
      this.spotLight2,
      this.spotLight4
    );
    this.scene.add(this.spaceStationScene);
  };

  initSpace = () => {
    const texture = this.supportsS3
      ? this.ddsLoader.load(
          `${process.env.PUBLIC_URL}/webgl_monochrome/textures/dds/space.dds`
        )
      : this.textureLoader.load(
          `${process.env.PUBLIC_URL}/webgl_monochrome/textures/space.jpg`
        );
    this.spaceBackground = new SpaceBackground(texture);
    this.spaceBackground.layers.set(2);
    this.spaceBackground.position.z = -499;
    this.scene.add(this.spaceBackground);
  };
  /*
  initStarfield = () => {
    this.starfield = new Starfield(this.textureLoader);
    this.starfield.layers.set(2);
    this.scene.add(this.starfield);

    this.nebula = new Nebula(this.textureLoader);
    this.nebula.layers.set(2);
    this.nebula.position.z = -499;
    this.scene.add(this.nebula);
  };
  */

  initPlanets = () => {
    const sunGeometry = new THREE.PlaneBufferGeometry(100, 100, 1, 1);
    const map = this.supportsS3
      ? this.ddsLoader.load(
          `${
            process.env.PUBLIC_URL
          }/webgl_monochrome/textures/dds/sun_lowcontrast.dds`
        )
      : this.textureLoader.load(
          `${
            process.env.PUBLIC_URL
          }/webgl_monochrome/textures/sun_lowcontrast.jpg`
        );
    const sunMaterial = new THREE.MeshBasicMaterial({
      map,
      transparent: true,
      blending: THREE.AdditiveBlending,
    });
    const sun = new THREE.Mesh(sunGeometry, sunMaterial);
    sun.scale.multiplyScalar(250);
    sun.position.x = 83000;
    sun.position.y = 73000;
    sun.position.z = -840000;
    this.scene.add(sun);

    const sphereGeometry = new THREE.SphereBufferGeometry(10, 16, 8);
    const sphereMaterial = new THREE.MeshBasicMaterial({ color: 0xffffff });
    if (this.occlusionEnabled) {
      const lightSphere = new THREE.Mesh(sphereGeometry, sphereMaterial);
      lightSphere.layers.set(OCCLUSION_LAYER);
      lightSphere.position.copy(sun.position);
      lightSphere.scale.copy(sun.scale);
      this.scene.add(lightSphere);
      this.lightSphere = lightSphere;
    }

    const planetMap = this.supportsS3
      ? this.ddsLoader.load(
          `${
            process.env.PUBLIC_URL
          }/webgl_monochrome/textures/dds/Mars_Seamless.dds`
        )
      : this.textureLoader.load(
          `${
            process.env.PUBLIC_URL
          }/webgl_monochrome/textures/Mars_Seamless.jpg`
        );
    this.planetScene = new PlanetScene(planetMap);
    this.spotLight.target = this.planetScene.children[0];
    this.scene.add(this.planetScene);

    const planetGeometry = new THREE.SphereBufferGeometry(6371, 40, 20);
    if (this.occlusionEnabled) {
      const material = new THREE.MeshBasicMaterial({ color: 0x000000 });
      const occlusionBox = new THREE.Mesh(planetGeometry.clone(), material);
      occlusionBox.layers.set(OCCLUSION_LAYER);
      occlusionBox.position.x = 20000;
      occlusionBox.position.y = 9000;
      occlusionBox.position.z = -150000;
      this.scene.add(occlusionBox);
    }

    this.sun = sun;

    const flare1 = this.supportsS3
      ? this.ddsLoader.load(
          `${process.env.PUBLIC_URL}/webgl_monochrome/textures/dds/flare1.dds`
        )
      : this.textureLoader.load(
          `${process.env.PUBLIC_URL}/webgl_monochrome/textures/flare1.png`
        );
    const flare2 = this.supportsS3
      ? this.ddsLoader.load(
          `${process.env.PUBLIC_URL}/webgl_monochrome/textures/dds/flare2.dds`
        )
      : this.textureLoader.load(
          `${process.env.PUBLIC_URL}/webgl_monochrome/textures/flare2.png`
        );
    const flare3 = this.supportsS3
      ? this.ddsLoader.load(
          `${process.env.PUBLIC_URL}/webgl_monochrome/textures/dds/flare3.dds`
        )
      : this.textureLoader.load(
          `${process.env.PUBLIC_URL}/webgl_monochrome/textures/flare3.png`
        );
    const lensFlare = new THREE.LensFlare(
      flare1,
      256,
      0.0,
      THREE.AdditiveBlending
    );
    lensFlare.add(flare2, 2048, 0.0, THREE.AdditiveBlending);
    lensFlare.add(flare3, 512, 1.0, THREE.AdditiveBlending);
    lensFlare.customUpdateCallback = this.lensFlareUpdateCallback;
    lensFlare.position.copy(sun.position);
    this.lensFlare = lensFlare;
    this.scene.add(lensFlare);
  };

  lensFlareUpdateCallback = object => {
    const fl = object.lensFlares.length;
    const vecX = -object.positionScreen.x * 2;
    const vecY = -object.positionScreen.y * 2;
    for (let f = 0; f < fl; f++) {
      const flare = object.lensFlares[f];
      flare.x = object.positionScreen.x + vecX * flare.distance;
      flare.y = object.positionScreen.y + vecY * flare.distance;
      flare.rotation = 0;
    }
    object.lensFlares[2].rotation = THREE.Math.degToRad(-50);
  };

  updateShaderLight = () => {
    if (!this.occlusionEnabled) return;

    const p = this.sun.position.clone(),
      vector = p.project(this.camera),
      x = (vector.x + 1) / 2,
      y = (vector.y + 1) / 2;
    this.volumetricLightPass.uniforms.lightPosition.value.set(x, y);
    this.lightSphere.position.copy(this.sun.position);
    this.lensFlare.position.copy(this.sun.position);
  };

  initGUI = () => {
    const gui = new dat.GUI();
    let folder, min, max, step;

    folder = gui.addFolder('Light Position');
    folder
      .add(this.sun.position, 'x')
      .min(-100000)
      .max(100000)
      .step(100)
      .onChange(this.updateShaderLight);
    folder
      .add(this.sun.position, 'y')
      .min(-100000)
      .max(100000)
      .step(100)
      .onChange(this.updateShaderLight);
    folder
      .add(this.sun.position, 'z')
      .min(-1000000)
      .max(1000000)
      .step(100)
      .onChange(this.updateShaderLight);
    folder.open();

    folder = gui.addFolder('Spot Light 1');
    folder
      .add(this.spotLight, 'intensity')
      .min(0)
      .max(10)
      .step(0.001);
    folder
      .add(this.spotLight, 'distance')
      .min(0)
      .max(2000000)
      .step(0.001);
    folder
      .add(this.spotLight, 'angle')
      .min(-Math.PI * 2)
      .max(Math.PI * 2)
      .step(0.0001);
    folder
      .add(this.spotLight, 'penumbra')
      .min(0)
      .max(2)
      .step(0.001);
    folder
      .add(this.spotLight, 'decay')
      .min(0)
      .max(4)
      .step(0.001);
    folder
      .add(this.spotLight.shadow, 'bias')
      .min(0)
      .max(0.001)
      .step(0.0000001);
    folder
      .add(this.spotLight.position, 'x')
      .min(-100000)
      .max(100000)
      .step(1);
    folder
      .add(this.spotLight.position, 'y')
      .min(-100000)
      .max(100000)
      .step(1);
    folder
      .add(this.spotLight.position, 'z')
      .min(-100000)
      .max(100000)
      .step(1);
    folder.open();

    folder = gui.addFolder('Spot Light 2');
    folder
      .add(this.spotLight2, 'intensity')
      .min(0)
      .max(10)
      .step(0.001);
    folder
      .add(this.spotLight2, 'distance')
      .min(0)
      .max(2000000)
      .step(0.001);
    folder
      .add(this.spotLight2, 'angle')
      .min(-Math.PI * 2)
      .max(Math.PI * 2)
      .step(0.0001);
    folder
      .add(this.spotLight2, 'penumbra')
      .min(0)
      .max(2)
      .step(0.001);
    folder
      .add(this.spotLight2, 'decay')
      .min(0)
      .max(4)
      .step(0.001);
    folder
      .add(this.spotLight2.shadow, 'bias')
      .min(0)
      .max(0.001)
      .step(0.0000001);
    folder
      .add(this.spotLight2.position, 'x')
      .min(-100000)
      .max(100000)
      .step(1);
    folder
      .add(this.spotLight2.position, 'y')
      .min(-100000)
      .max(100000)
      .step(1);
    folder
      .add(this.spotLight2.position, 'z')
      .min(-100000)
      .max(100000)
      .step(1);
    folder.open();

    folder = gui.addFolder('Volumetric Light Shader');
    Object.keys(this.volumetricLightPass.uniforms).forEach(key => {
      if (key !== 'tDiffuse' && key !== 'lightPosition') {
        let prop = this.volumetricLightPass.uniforms[key];

        switch (key) {
          case 'exposure':
            min = 0;
            max = 1;
            step = 0.01;
            break;
          case 'decay':
            min = 0.8;
            max = 1;
            step = 0.001;
            break;
          case 'density':
            min = 0;
            max = 1;
            step = 0.01;
            break;
          case 'weight':
            min = 0;
            max = 1;
            step = 0.01;
            break;
          case 'samples':
            min = 1;
            max = 100;
            step = 1.0;
            break;
          default:
            break;
        }

        folder
          .add(prop, 'value')
          .min(min)
          .max(max)
          .step(step)
          .name(key);
      }
    });
    folder.open();

    folder = gui.addFolder('transition progress');
    folder
      .add(this.mixPass.uniforms.uThreshold, 'value')
      .min(0)
      .max(1)
      .step(0.0001);
    folder
      .add(this.mixPass.uniforms.uProgress, 'value')
      .min(0)
      .max(1)
      .step(0.0001);
    folder.open();
  };

  componentWillUnmount() {
    this.stop();
    this.mount.removeChild(this.renderer.domElement);
  }

  start = () => {
    if (!this.frameId) {
      this.frameId = requestAnimationFrame(this.animate);
      this.initialRender = true;

      requestAnimationFrame(() => {
        this.props.onWebGlLoaded();
      });
    }
  };

  stop = () => {
    cancelAnimationFrame(this.frameId);
  };

  updateProgress = val => {
    //this.progress = val;
    this.progress = Math.max(0, Math.min(1, val.toFixed(2) * 2));
    if (this.disableConstantRender) {
      this.frameId = requestAnimationFrame(this.animate);
    }
  };

  animate() {
    /*
    if (!this.lastUpdate) {
      this.lastUpdate = performance.now();
      this.averageFPS = 60;
    } else {
      const now = performance.now(),
        frameDelay = now - this.lastUpdate,
        fpsNow = 1000.0 / frameDelay;

      this.lastUpdate = now;
      if (frameDelay > 0 && fpsNow > 1) {
        this.averageFPS += (fpsNow - this.averageFPS) * 0.005;
        console.log(this.averageFPS);
        if (this.averageFPS < FPS_THRESHOLD) this.disableConstantRender = true;
      }
    }*/

    if (this.disableConstantRender && this.lastProgress === this.progress) {
      return;
    } else {
      this.frameId = requestAnimationFrame(this.animate);
    }

    if (statsEnabled) this.stats.begin();
    const progress = this.progress;
    /*
    if (this.deltaEase < 1) {
      this.deltaEase = easeInCubic(this.ease);
      this.ease += 1000 / 60 * 0.001;
    }
*/
    const currentTime = Date.now();
    const delta = (currentTime - lastTime) / 1000;
    const deltaMultiplier = delta / (1000 / 60 * 0.001);
    //    const deltaMultiplier = delta / (1000 / 60 * 0.001) * this.deltaEase;

    this.planetScene.update(deltaMultiplier);
    this.asteroidScene.update(deltaMultiplier);
    this.spaceStationScene.update(deltaMultiplier);

    this.mixPass.uniforms.uProgress.value = 1.0 - progress;

    const sunProgress = 1 - Math.max(0, Math.min(1, Math.pow(progress, 2.5)));

    const destScale = 250 + 500 * sunProgress;
    if (this.sunScale !== destScale) {
      this.sun.scale.set(destScale, destScale, destScale);
      this.sunScale = destScale;
      if (this.occlusionEnabled) {
        this.lightSphere.scale.set(destScale, destScale, destScale);
      }
    }

    if (this.progress > 0 || this.initialRender) {
      if (!this.bgPass.enabled) {
        this.bgPass.enabled = true;
        this.fgPass.enabled = true;
        this.additivePass.enabled = true;
      }
    } else {
      if (this.bgPass.enabled) {
        this.bgPass.enabled = false;
        this.fgPass.enabled = false;
        this.additivePass.enabled = false;
      }
    }

    this.renderScene();

    lastTime = currentTime;

    if (statsEnabled) this.stats.end();

    this.lastProgress = this.progress;
  }

  renderScene() {
    if (this.progress < 1) {
      this.camera.layers.set(100);
      this.renderer.setClearColor(0xffffff);
      this.renderer.render(this.scene, this.camera, this.wireRenderTarget);
    }
    // render the occlusion scene and apply the volumetric light shader
    if (this.occlusionEnabled && (this.progress > 0 || this.initialRender)) {
      // show the objects in the occlusion scene
      this.camera.layers.set(OCCLUSION_LAYER);
      // set a black background for the render
      this.renderer.setClearColor(0x000000);
      this.occlusionComposer.render();
      this.initialRender = false;
    }

    // show the objects in the lit scene
    this.camera.layers.set(DEFAULT_LAYER);
    // render the lit scene and blend the volumetric light effect
    this.composer.render();
  }

  render() {
    return (
      <Container
        innerRef={mount => {
          this.mount = mount;
        }}
      />
    );
  }
}

export default SpaceScene;



// WEBPACK FOOTER //
// ./src/modules/parallax/webgl/SpaceScene.js